/**
 * EmailBody — Web platform (dangerouslySetInnerHTML + DOMPurify)
 * Matches web frontend inbox sanitization behavior.
 */
import { useState } from 'react'
import { View, Text, Pressable, StyleSheet } from 'react-native'
import DOMPurify from 'dompurify'
import { colors, spacing, typography } from '../theme'

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'a', 'ul', 'ol', 'li',
  'h1', 'h2', 'h3', 'h4', 'span', 'div', 'table', 'tbody', 'thead',
  'tr', 'td', 'th', 'img', 'blockquote',
]
const ALLOWED_ATTR = ['href', 'src', 'alt', 'class', 'target', 'rel']

// Enforce rel="noopener noreferrer" on all links to prevent tab-napping
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

interface EmailBodyProps {
  html: string
  plainText?: string
  style?: any
  maxPreview?: number
}

export default function EmailBody({ html, plainText, style, maxPreview = 200 }: EmailBodyProps) {
  const text = plainText || html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
  const isLong = text.length > maxPreview
  const [expanded, setExpanded] = useState(!isLong)
  const hasHtml = html && html.trim().startsWith('<')

  const sanitizedHtml = hasHtml
    ? DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOW_DATA_ATTR: false })
    : ''

  return (
    <View style={[styles.container, style]}>
      {expanded && hasHtml ? (
        <div
          className="prose prose-xs"
          style={{
            fontSize: 13,
            lineHeight: 1.6,
            color: colors.text.secondary,
            wordBreak: 'break-word' as const,
            overflowWrap: 'break-word' as const,
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      ) : (
        <Text style={styles.bodyText}>
          {isLong && !expanded ? text.slice(0, maxPreview) + '...' : text}
        </Text>
      )}
      {isLong && (
        <Pressable onPress={() => setExpanded(!expanded)} hitSlop={8}>
          <Text style={styles.toggleText}>{expanded ? 'Vis mindre' : 'Vis hele besked'}</Text>
        </Pressable>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: spacing.sm,
  },
  bodyText: { ...typography.small, color: colors.text.secondary, lineHeight: 18 },
  toggleText: { fontSize: 11, color: '#2563eb', fontWeight: '500', marginTop: 4 },
})
